import type { IPluginOptions } from "@/schemas/pluginOptions/pluginOptionsSchema";
import { templateSettings } from "@/store/templateSetting";
import { templateStyle } from "@/store/templateStyle";
import { defaultTemplate } from "@/store/defaultTemplate";
import { mergeDeep } from "@/utils/mergeDeep";

type DefaultTemplateSettingsOptions = NonNullable<
  IPluginOptions["defaultTemplateSettings"]
>;

// Snapshot the original baseline so repeated calls are idempotent
const baselineSettings = JSON.parse(JSON.stringify(templateSettings));
const baselineStyle = JSON.parse(JSON.stringify(templateStyle));

/**
 * Merges plugin options into templateSettings and creates a default template.
 *
 * Handles both legacy options (emailWidth, buttonsBackgroundColor, ...)
 * and new block-specific options (button, carousel, divider, ...).
 */
export function applyDefaultTemplateSettings(
  defSet: DefaultTemplateSettingsOptions
) {
  // Reset to baseline so previous calls don't accumulate
  Object.keys(templateSettings).forEach(
    (key) => delete (templateSettings as any)[key]
  );
  Object.assign(templateSettings, JSON.parse(JSON.stringify(baselineSettings)));

  Object.keys(templateStyle).forEach(
    (key) => delete (templateStyle as any)[key]
  );
  Object.assign(templateStyle, JSON.parse(JSON.stringify(baselineStyle)));
  if (defSet.emailWidth) {
    templateSettings.containerWidth = defSet.emailWidth;
  }
  if (defSet.emailBackgroundColor) {
    templateSettings.templateBackgroundColor = defSet.emailBackgroundColor;
  }
  if (defSet.blockBackgroundColor) {
    templateSettings["mj-class"] = {
      name: "section",
      "background-color": defSet.blockBackgroundColor,
    };
    if (!templateSettings["mj-section"]) {
      templateSettings["mj-section"] = {};
    }
    templateSettings["mj-section"]["background-color"] =
      defSet.blockBackgroundColor;
  }
  if (defSet.buttonsBackgroundColor) {
    if (!templateSettings["mj-button"]) {
      templateSettings["mj-button"] = {};
    }
    templateSettings["mj-button"]["background-color"] =
      defSet.buttonsBackgroundColor;
  }
  if (defSet.hyperlinkColor) {
    mergeDeep(templateStyle, {
      a: {
        color: defSet.hyperlinkColor,
      },
    });
  }
  if (defSet.h1) {
    mergeDeep(templateStyle, {
      h1: defSet.h1,
    });
  }
  if (defSet.h2) {
    mergeDeep(templateStyle, {
      h2: defSet.h2,
    });
  }
  if (defSet.h3) {
    mergeDeep(templateStyle, {
      h3: defSet.h3,
    });
  }
  if (defSet.p) {
    mergeDeep(templateStyle, {
      p: defSet.p,
    });
  }
  if (defSet.text) {
    mergeDeep(templateSettings, {
      "mj-text": defSet.text,
    });
  }
  if (defSet.button) {
    mergeDeep(templateSettings, {
      "mj-button": defSet.button,
    });
  }
  if (defSet.carousel) {
    mergeDeep(templateSettings, {
      "mj-carousel": defSet.carousel,
    });
  }
  if (defSet.divider) {
    mergeDeep(templateSettings, {
      "mj-divider": defSet.divider,
    });
  }
  if (defSet.gif) {
    mergeDeep(templateSettings, {
      "mj-gif": defSet.gif,
    });
  }
  if (defSet.image) {
    mergeDeep(templateSettings, {
      "mj-image": defSet.image,
    });
  }
  if (defSet.loop) {
    mergeDeep(templateSettings, {
      "mj-loop": defSet.loop,
    });
  }
  if (defSet.social) {
    mergeDeep(templateSettings, {
      "mj-social": defSet.social,
    });
  }
  if (defSet.column) {
    mergeDeep(templateSettings, {
      "mj-column": defSet.column,
    });
  }
  if (defSet.section) {
    mergeDeep(templateSettings, {
      "mj-section": defSet.section,
    });
    if (defSet.section["background-color"]) {
      templateSettings["mj-class"] = {
        name: "section",
        "background-color": defSet.section["background-color"],
      };
    }
  }
  if (defSet["line-height"]) {
    templateSettings[":line-height"] = defSet["line-height"].toString();

    mergeDeep(templateSettings["mj-text"], {
      "line-height": defSet["line-height"],
    });
  }
  if (defSet["structure-margin"]) {
    if (!templateSettings["mj-section"]) {
      templateSettings["mj-section"] = {};
    }
    templateSettings["mj-section"]["padding"] = defSet["structure-margin"];
  }
  if (defSet["stack-columns"] !== undefined) {
    const stuckColumns = defSet["stack-columns"];
    if (!templateSettings["mj-section"]) {
      templateSettings["mj-section"] = {};
    }
    templateSettings["mj-section"]["in-group"] = !stuckColumns;
  }
  return defaultTemplate(templateSettings, templateStyle);
}
